import * as React from "react";
import { Form, Params, redirect, useActionData, useLoaderData, useNavigate, useSubmit } from "react-router-dom";
import {
    Button, Message,
    Modal,
    Table, TableBody, TableCell,
    TableHeader, TableHeaderCell, TableRow
} from "semantic-ui-react";
import "./creation-area-edit.css";
import { RootStore } from "../../../stores/root-store";
import { CreationArea, UsageArea } from "../../../stores/area/area";
import LabeledTextArea from "../../../components/labeled-textarea/labeled-textarea";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import MyDatePicker from "../../../components/date-picker/date-picker";
import LabeledValue from "../../../components/labeled-value/labeled-value";
import { KeyValueStore } from "../../../stores/key-value/key-value-store";
import { TransportError } from "../../../stores/data-transporter";
import { makeCreationAreaDto } from "../../../stores/area/area-dto";
import { Constants } from "../../../globals/constants";
import { getLabel } from "../../../reflection/reflection";

type LoaderResult = {
    area: CreationArea;
    keyValueStore: KeyValueStore;
}

export async function loader(
    params: Params,
    rootStore: RootStore
): Promise<LoaderResult> {
    if (params.areaId !== undefined) {
        const creationAreaId = parseInt(params.areaId) || 0;
        const rootStoreSession = rootStore.newSession();
        const area = await rootStoreSession.areaStore.loadCreationArea(creationAreaId);
        await rootStoreSession.projectStore.loadCreationProject(area.projectId);
        const keyValueStore = rootStoreSession.keyValueStore;
        return { area, keyValueStore }
    }

    throw new Response("", {
        status: 404,
        statusText: "Neuschaffungsfläche wurde nicht gefunden",
    });
}

export async function action(request: Request, rootStore: RootStore) {
    const formData = await request.formData();
    const creationAreaDto = makeCreationAreaDto(formData);
    const valid = "creationStatus" in creationAreaDto;

    if (valid) {
        try {
            const rootStoreSession = rootStore.session;
            const creationArea = await rootStoreSession.areaStore.updateCreationArea(creationAreaDto);

            if (creationArea.project.typeKey === Constants.projectTypeRecultivation) {
                await rootStoreSession.compensationStore.loadWithOptions({ creationProjectId: creationArea.projectId });
                const usageProject = await rootStoreSession.projectStore.loadUsageProject(creationArea.project.compensations[0].usageProjectId);
                return redirect(`/projekte/${usageProject.categoryKey}/${usageProject.id}`);
            }

            return redirect(`/projekte/${creationArea.project.categoryKey}/${creationArea.projectId}`);
        } catch (e: TransportError) {
            switch (e.statusCode) {
                case 400:
                    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
                default:
                    return "Es ist ein unerwarteter Fehler aufgetreten!";
            }
        }
    }

    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
}

export default function CreationAreaEdit(): JSX.Element {
    const { area, keyValueStore } = useLoaderData() as LoaderResult;
    const navigate = useNavigate();
    const submit = useSubmit();
    const errorMsg = useActionData() as any;

    return (
        <Modal open={true} size="large">
            <Modal.Header>Flächen ID: {area.id}</Modal.Header>
            <Modal.Content scrolling={true}>
                <Table compact="very" basic="very" size="small">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEK2")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEK3")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEK4")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEK5")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEK6")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(UsageArea.prototype, "areaNEKUnknown")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(area, "eligibilityDisplayText")}</TableHeaderCell>
                            <TableHeaderCell
                                textAlign="right">{getLabel(area, "effectiveAreaRounded")}</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell
                                textAlign="right">{area.nek === 2 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell
                                textAlign="right">{area.nek === 3 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell
                                textAlign="right">{area.nek === 4 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell
                                textAlign="right">{area.nek === 5 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell
                                textAlign="right">{area.nek === 6 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell
                                textAlign="right">{area.nek === 99 ? area.areaRounded.toLocaleString() : 0}</TableCell>
                            <TableCell textAlign="right">{area.eligibilityDisplayText}</TableCell>
                            <TableCell textAlign="right">{area.effectiveAreaRounded.toLocaleString()}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <br/>
                <Form method="post" id="editCreationAreaForm">
                    <div className="creationAreaEdit">
                        {errorMsg !== undefined && <Message negative>{errorMsg}</Message>}
                        <LabeledValue
                            label="Etappe"
                            value={area.stage}
                        />
                        <LabeledValue
                            label="Realisierte PNG [cm]"
                            value={area.png}
                        />
                        <LabeledValue
                            label="Realisierte NEK"
                            value={area.nek}
                        />
                        <LabeledValue
                            label="Anrechenbarkeit"
                            value={area.eligibility}
                        />
                        <LabeledInput
                            label="Status Schaffung"
                            inputElement={
                                <select name="creationStatus" className="ui selection dropdown">
                                    <option disabled selected={area.areaDto.creationStatus === null}>
                                        -- Auswählen --
                                    </option>
                                    {keyValueStore.compensationStatuses.map((s) => (
                                        <option
                                            key={s.key}
                                            value={s.key}
                                            selected={area.areaDto.creationStatus === s.key}
                                        >
                                            {s.value}
                                        </option>
                                    ))}
                                </select>
                            }
                            required={true}
                        />
                        <LabeledInput
                            label="Beginn Bodenarbeiten"
                            inputElement={<MyDatePicker name="soilWorkStartDate"
                                                        value={area.soilWorkStartDate}/>}
                        />
                        <LabeledInput
                            label="Ende Bodenarbeiten"
                            inputElement={<MyDatePicker name="soilWorkEndDate" value={area.soilWorkEndDate}/>}
                        />
                        <LabeledInput
                            label="Abnahme nach Bodenauftrag"
                            inputElement={<MyDatePicker name="afterSoilApplicationAcceptanceDate"
                                                        value={area.afterSoilApplicationAcceptanceDate}/>}
                        />
                        <LabeledInput
                            label="Ende Folgebewirtschaftung"
                            inputElement={<MyDatePicker name="cultivationEndDate"
                                                        value={area.cultivationEndDate}/>}
                        />
                        <LabeledInput
                            label="Abnahme nach Folgebewirtschaftung"
                            inputElement={<MyDatePicker name="afterCultivationAcceptanceDate"
                                                        value={area.afterCultivationAcceptanceDate}/>}
                        />
                        <LabeledTextArea label="Bemerkungen Projektstand" name="remarksProjectStatus"
                                         defaultValue={area.remarksProjectStatus}/>

                        <LabeledInput
                            label="Stand Dokumentation nach Bodenauftrag"
                            inputElement={
                                <select name="afterSoilApplicationDocStatus" className="ui selection dropdown">
                                    <option disabled selected={area.areaDto.afterSoilApplicationDocStatus === null}>
                                        -- Auswählen --
                                    </option>
                                    {keyValueStore.conditionControlStatuses.map((s) => (
                                        <option
                                            key={s.key}
                                            value={s.key}
                                            selected={area.afterSoilApplicationDocStatus === s.key}
                                        >
                                            {s.value}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                        <LabeledInput
                            label="Quelle Dokumentation nach Bodenauftrag"
                            inputElement={
                                <select name="afterSoilApplicationDocSource" className="ui selection dropdown">
                                    <option disabled selected={area.areaDto.afterSoilApplicationDocSource === null}>
                                        -- Auswählen --
                                    </option>
                                    {keyValueStore.conditionControlSources.map((s) => (
                                        <option
                                            key={s.key}
                                            value={s.key}
                                            selected={area.afterSoilApplicationDocSource === s.key}
                                        >
                                            {s.value}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                        <LabeledInput
                            label="Stand Dokumentation nach Folgebewirtschaftung"
                            inputElement={
                                <select name="afterCultivationDocStatus" className="ui selection dropdown">
                                    <option disabled selected={area.areaDto.afterCultivationDocStatus === null}>
                                        -- Auswählen --
                                    </option>
                                    {keyValueStore.conditionControlStatuses.map((s) => (
                                        <option
                                            key={s.key}
                                            value={s.key}
                                            selected={area.afterCultivationDocStatus === s.key}
                                        >
                                            {s.value}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                        <LabeledInput
                            label="Quelle Dokumentation nach Folgebewirtschaftung"
                            inputElement={
                                <select name="afterCultivationDocSource" className="ui selection dropdown">
                                    <option disabled selected={area.areaDto.afterCultivationDocSource === null}>
                                        -- Auswählen --
                                    </option>
                                    {keyValueStore.conditionControlSources.map((s) => (
                                        <option
                                            key={s.key}
                                            value={s.key}
                                            selected={area.afterCultivationDocSource === s.key}
                                        >
                                            {s.value}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                        <LabeledTextArea label="Bemerkungen Dokumentation" name="remarksDocumentation"
                                         defaultValue={area.remarksDocumentation}/>
                        <input type="hidden" name="id" value={area.id}/>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={() => navigate(-1)}>Abbrechen</Button>
                <Button type="button"
                        onClick={() => submit(document.getElementById("editCreationAreaForm") as HTMLFormElement)}>
                    Speichern
                </Button>
            </Modal.Actions>
        </Modal>
    )
        ;
}
